import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Copy, Check, Mail, Key, Eye, RefreshCw, Sparkles } from 'lucide-react';
import { appApi } from '@/api/client';
import { CARD_TEMPLATES, renderCardEmailHtml } from '@/lib/cardTemplates';
import type { App } from '@/types';

export default function Apps() {
  const [apps, setApps] = useState<App[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingApp, setEditingApp] = useState<App | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email_template_title: '',
    email_template_content: '',
    card_template: 'random' as string,
    card_templates_enabled: [] as string[],
  });
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // 预览相关状态
  const [previewTemplateId, setPreviewTemplateId] = useState<string>('');
  const [previewHtml, setPreviewHtml] = useState<string>('');

  useEffect(() => {
    fetchApps();
  }, []);

  const fetchApps = async () => {
    setLoading(true);
    try {
      const response = await appApi.getAll();
      if (response.success && response.data) {
        setApps(response.data);
      }
    } catch {
      console.error('Failed to fetch apps');
    } finally {
      setLoading(false);
    }
  };

  // 生成预览 HTML（前端本地渲染，调用前端 renderCardEmailHtml）
  const generateLocalPreview = (templateId: string, appName: string) => {
    const template = CARD_TEMPLATES.find(t => t.id === templateId) || CARD_TEMPLATES[0];
    return renderCardEmailHtml(template, { code: '831204', appName: appName || '示例应用', minutes: 5 });
  };

  // 刷新预览
  const refreshPreview = () => {
    const templateId = previewTemplateId || (formData.card_template !== 'random' && formData.card_template !== 'plain' ? formData.card_template : '');
    if (templateId) {
      setPreviewHtml(generateLocalPreview(templateId, formData.name));
    } else {
      // random 模式：本地随机一个
      const randomTemplate = CARD_TEMPLATES[Math.floor(Math.random() * CARD_TEMPLATES.length)];
      setPreviewHtml(generateLocalPreview(randomTemplate.id, formData.name));
    }
  };

  // 选择某个模板进行预览
  const handlePreviewTemplate = (templateId: string) => {
    setPreviewTemplateId(templateId);
    setPreviewHtml(generateLocalPreview(templateId, formData.name));
  };

  // 应用名变化时同步预览中的应用名
  useEffect(() => {
    if (showModal) {
      const templateId = previewTemplateId || (formData.card_template !== 'random' && formData.card_template !== 'plain' ? formData.card_template : '');
      if (templateId) {
        setPreviewHtml(generateLocalPreview(templateId, formData.name));
      }
    }
  }, [formData.name, showModal]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingApp) {
        await appApi.update(editingApp.id, formData);
      } else {
        await appApi.create({ ...formData, secret_key: '' } as Partial<App>);
      }
      setShowModal(false);
      setEditingApp(null);
      resetForm();
      fetchApps();
    } catch {
      alert('操作失败');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email_template_title: '',
      email_template_content: '',
      card_template: 'random',
      card_templates_enabled: [],
    });
    setPreviewTemplateId('');
    setPreviewHtml('');
  };

  const handleDelete = async (id: string) => {
    if (confirm('确定要删除这个应用吗？')) {
      try {
        await appApi.delete(id);
        fetchApps();
      } catch {
        alert('删除失败');
      }
    }
  };

  const handleCopy = async (text: string, id: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const openEditModal = (app: App) => {
    setEditingApp(app);
    setFormData({
      name: app.name,
      email_template_title: app.email_template_title || '',
      email_template_content: app.email_template_content || '',
      card_template: app.card_template || 'random',
      card_templates_enabled: app.card_templates_enabled || [],
    });
    setShowModal(true);
    // 默认预览应用当前配置的模板（若 random 则展示第一个）
    const defaultPreviewId = app.card_template && app.card_template !== 'random' && app.card_template !== 'plain'
      ? app.card_template
      : (app.card_templates_enabled && app.card_templates_enabled.length ? app.card_templates_enabled[0] : CARD_TEMPLATES[0].id);
    setPreviewTemplateId(defaultPreviewId);
    setPreviewHtml(generateLocalPreview(defaultPreviewId, app.name));
  };

  const openNewModal = () => {
    resetForm();
    setEditingApp(null);
    setShowModal(true);
    // 默认展示一个随机模板的预览
    const randomTemplate = CARD_TEMPLATES[Math.floor(Math.random() * CARD_TEMPLATES.length)];
    setPreviewTemplateId(randomTemplate.id);
    setPreviewHtml(generateLocalPreview(randomTemplate.id, ''));
  };

  // 切换启用模板
  const toggleEnabledTemplate = (templateId: string) => {
    setFormData(prev => {
      const enabled = prev.card_templates_enabled.includes(templateId)
        ? prev.card_templates_enabled.filter(id => id !== templateId)
        : [...prev.card_templates_enabled, templateId];
      return { ...prev, card_templates_enabled: enabled };
    });
  };

  // 获取应用当前生效模板的名称（用于列表中展示）
  const getTemplateBadge = (app: App) => {
    const ct = app.card_template;
    if (!ct || ct === 'random') {
      const enabledCount = app.card_templates_enabled?.length || 0;
      return enabledCount > 0 ? `随机·${enabledCount}种` : '随机·全部';
    }
    if (ct === 'plain') return '纯文本';
    const tpl = CARD_TEMPLATES.find(t => t.id === ct);
    return tpl ? tpl.name : ct;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">应用管理</h1>
          <p className="text-slate-400 mt-1">管理所有接入的应用程序与邮件名片模板</p>
        </div>
        <button
          onClick={openNewModal}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-all"
        >
          <Plus className="w-5 h-5" />
          新建应用
        </button>
      </div>

      <div className="bg-slate-800 rounded-xl border border-slate-700/50 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-700/50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">应用名称</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">APP ID</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">APP Secret</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">邮件标题</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">名片模板</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">创建时间</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <div className="inline-flex items-center gap-2 text-slate-400">
                      <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      加载中...
                    </div>
                  </td>
                </tr>
              ) : apps.length > 0 ? (
                apps.map((app) => (
                  <tr key={app.id} className="hover:bg-slate-700/30 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                          <Key className="w-4 h-4 text-white" />
                        </div>
                        <span className="text-white font-medium">{app.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <code className="text-slate-300 text-sm bg-slate-700 px-2 py-1 rounded">
                          {app.id}
                        </code>
                        <button
                          onClick={() => handleCopy(app.id, `id-${app.id}`)}
                          className="text-slate-400 hover:text-white transition-colors"
                        >
                          {copiedId === `id-${app.id}` ? (
                            <Check className="w-4 h-4 text-green-400" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <code className="text-slate-300 text-sm bg-slate-700 px-2 py-1 rounded font-mono">
                          {app.secret_key?.slice(0, 8)}...
                        </code>
                        <button
                          onClick={() => handleCopy(app.secret_key, `secret-${app.id}`)}
                          className="text-slate-400 hover:text-white transition-colors"
                        >
                          {copiedId === `secret-${app.id}` ? (
                            <Check className="w-4 h-4 text-green-400" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-slate-400">
                        <Mail className="w-4 h-4" />
                        <span className="text-sm truncate max-w-xs">{app.email_template_title}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-500/10 text-purple-300 text-xs rounded-full">
                        <Sparkles className="w-3 h-3" />
                        {getTemplateBadge(app)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-slate-400 text-sm">
                        {new Date(app.created_at).toLocaleString('zh-CN')}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openEditModal(app)}
                          className="p-2 text-slate-400 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition-all"
                          title="编辑"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(app.id)}
                          className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
                          title="删除"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-slate-400">
                    暂无应用数据，点击上方按钮创建
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-slate-800 rounded-xl w-full max-w-5xl border border-slate-700/50 my-8">
            <div className="p-6 border-b border-slate-700/50 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">
                {editingApp ? '编辑应用' : '新建应用'}
              </h2>
              <span className="text-xs text-slate-400 flex items-center gap-1">
                <Sparkles className="w-3 h-3 text-purple-400" />
                邮件名片效果支持 8 种主题 + 随机切换
              </span>
            </div>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
              {/* 左列：表单字段 */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">应用名称</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">邮件标题模板</label>
                  <input
                    type="text"
                    value={formData.email_template_title}
                    onChange={(e) => setFormData({ ...formData, email_template_title: e.target.value })}
                    placeholder="例如：【{app_name}】验证码"
                    className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">邮件内容模板（纯文本回退选项）</label>
                  <textarea
                    value={formData.email_template_content}
                    onChange={(e) => setFormData({ ...formData, email_template_content: e.target.value })}
                    placeholder="例如：您的验证码是 {code}，有效期5分钟。"
                    rows={3}
                    className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <div className="text-xs text-slate-500 mt-1">
                    模板变量：{`{app_name}`}（应用名称）、{`{code}`}（验证码）。选择「纯文本」模式时使用此内容。
                  </div>
                </div>

                {/* 名片模板选择 */}
                <div className="pt-2 border-t border-slate-700/50">
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    邮件名片模板（验证码邮件整体外观）
                  </label>
                  <div className="flex gap-2 mb-3">
                    <select
                      value={formData.card_template}
                      onChange={(e) => setFormData({ ...formData, card_template: e.target.value })}
                      className="px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="random">随机切换（每次发送都换一种风格）</option>
                      <option value="plain">纯文本（使用上面的「邮件内容模板」，不套名片）</option>
                      {CARD_TEMPLATES.map(t => (
                        <option key={t.id} value={t.id}>固定使用：{t.name}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={refreshPreview}
                      className="flex items-center gap-1 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm transition-all"
                      title="刷新预览（用于 random 模式随机看效果）"
                    >
                      <RefreshCw className="w-4 h-4" />
                      换一张
                    </button>
                  </div>

                  {/* 启用的模板列表：仅在 random 模式下有意义 */}
                  {formData.card_template === 'random' && (
                    <div className="space-y-2">
                      <div className="text-xs text-slate-400">
                        勾选参与随机切换的模板（不勾选则表示全部 8 种都参与）：
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {CARD_TEMPLATES.map(t => {
                          const enabled = formData.card_templates_enabled.length === 0 || formData.card_templates_enabled.includes(t.id);
                          return (
                            <button
                              type="button"
                              key={t.id}
                              onClick={() => handlePreviewTemplate(t.id)}
                              className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs transition-all ${
                                previewTemplateId === t.id
                                  ? 'border-purple-500 bg-purple-500/10 text-white'
                                  : 'border-slate-700 bg-slate-700/30 text-slate-300 hover:border-slate-600'
                              }`}
                            >
                              <span
                                className="w-4 h-4 rounded-full flex-shrink-0 border border-white/20"
                                style={{ background: t.background }}
                              />
                              <span className="flex-1 truncate">{t.name}</span>
                              <input
                                type="checkbox"
                                checked={enabled}
                                onChange={(e) => { e.stopPropagation(); toggleEnabledTemplate(t.id); }}
                                onClick={(e) => e.stopPropagation()}
                                className="ml-1"
                              />
                            </button>
                          );
                        })}
                      </div>
                      <div className="text-xs text-slate-500">
                        点击任一模板卡片可在右侧预览其效果；勾选框用于控制是否参与随机。
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingApp(null);
                      resetForm();
                    }}
                    className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-all"
                  >
                    取消
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-all"
                  >
                    {editingApp ? '保存' : '创建'}
                  </button>
                </div>
              </div>

              {/* 右列：预览 */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-slate-300 flex items-center gap-1">
                    <Eye className="w-4 h-4" />
                    名片效果预览
                  </label>
                  <button
                    type="button"
                    onClick={refreshPreview}
                    className="text-xs text-slate-400 hover:text-white flex items-center gap-1"
                  >
                    <RefreshCw className="w-3 h-3" />
                    随机一张
                  </button>
                </div>
                <div className="bg-white rounded-lg overflow-hidden border border-slate-700/50 min-h-[520px]">
                  {previewHtml ? (
                    <iframe
                      title="邮件名片预览"
                      srcDoc={previewHtml}
                      className="w-full h-[520px] border-0"
                    />
                  ) : (
                    <div className="p-12 text-center text-slate-500 text-sm">
                      请选择一个模板查看预览
                    </div>
                  )}
                </div>
                <div className="text-xs text-slate-500">
                  提示：实际发送的验证码是 6 位数字。预览中显示的 <code className="bg-slate-700 px-1 rounded">831204</code> 仅为示例。
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
